import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import LessonCard from "../components/LessonCard";
import SuggestTopicDialog from "../components/SuggestTopicDialog";
import {
  XMarkIcon,
  ChevronDownIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import BackButton from "../components/BackButton";
import { isSuperAdmin } from "../utils/users";

const AGE_GROUP_OPTIONS = ["7-9", "9-11", "11-12"];

function Lessons({ userType = "user" }) {
  const [lessons, setLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [selectedAgeGroups, setSelectedAgeGroups] = useState([]);
  const [showAgeGroupDropdown, setShowAgeGroupDropdown] = useState(false);
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const ageGroupDropdownRef = useRef(null);

  // Close age group dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ageGroupDropdownRef.current &&
        !ageGroupDropdownRef.current.contains(event.target)
      ) {
        setShowAgeGroupDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch user's completed lessons from user_activity collection
  useEffect(() => {
    const fetchCompletedLessons = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          return;
        }

        const activityRef = collection(db, "user_activity");
        const q = query(
          activityRef,
          where("user_id", "==", doc(db, "users", currentUser.uid)),
          where("type", "==", "completed_lesson"),
        );
        const querySnapshot = await getDocs(q);

        const completedIds = new Set();
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.lesson_id) {
            completedIds.add(data.lesson_id);
          }
        });

        console.log("Found completed lesson IDs:", Array.from(completedIds));

        setCompletedLessons(completedIds);
      } catch (error) {
        console.error("Error fetching completed lessons:", error);
        // Don't set error state - completion tracking failure shouldn't prevent lessons from loading
      }
    };

    fetchCompletedLessons();
  }, []);

  // Fetch lessons from Firestore
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const lessonsRef = collection(db, "node_lessons");
        const q = query(lessonsRef, orderBy("title"));
        const querySnapshot = await getDocs(q);

        const lessonsData = [];
        querySnapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        // Filter lessons based on user type
        // Super admins can see all lessons (including preview), others only see published lessons
        const filteredLessonsData = isSuperAdmin(userType)
          ? lessonsData
          : lessonsData.filter((lesson) => lesson.isPreview !== true);

        setLessons(filteredLessonsData);
        setFilteredLessons(filteredLessonsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lessons:", error);
        setError("Failed to load lessons");
        setLoading(false);
      }
    };

    fetchLessons();
  }, [userType]);

  // Filter lessons based on search term and age groups
  useEffect(() => {
    let filtered = lessons;

    // Filter by age groups
    if (selectedAgeGroups.length > 0) {
      filtered = filtered.filter((lesson) => {
        const lessonAgeGroups = (lesson.age_groups || []).map((item) =>
          typeof item === "string" ? item : (item && item.description) || "",
        );
        return selectedAgeGroups.some((ag) => lessonAgeGroups.includes(ag));
      });
    }

    // Filter by search term
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((lesson) => {
        const titleMatch = (lesson.title || "").toLowerCase().includes(term);
        const keywords = lesson.keywords || [];
        const keywordMatch = keywords.some((kw) =>
          (kw || "").toLowerCase().includes(term),
        );
        const units = lesson.units || [];
        const topicMatch = units.some((u) =>
          (typeof u === "string" ? u : (u && u.description) || "")
            .toLowerCase()
            .includes(term),
        );
        return titleMatch || keywordMatch || topicMatch;
      });
    }

    setFilteredLessons(filtered);
  }, [searchTerm, selectedAgeGroups, lessons]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleAgeGroupToggle = (option) => {
    setSelectedAgeGroups((prev) =>
      prev.includes(option)
        ? prev.filter((ag) => ag !== option)
        : [...prev, option],
    );
  };

  const getAgeGroupFilterText = () => {
    if (selectedAgeGroups.length === 0) return "Age Groups";
    if (selectedAgeGroups.length === AGE_GROUP_OPTIONS.length)
      return "All Ages";
    return `${selectedAgeGroups.length} age group${selectedAgeGroups.length === 1 ? "" : "s"}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Lessons Section */}
      <div className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <BackButton to="/" />

          {/* Header with Title and Search */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
            <h1 className="text-3xl font-bold">Lessons</h1>

            {/* Age Group Filter and Search */}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {/* Age Group Dropdown */}
              <div className="relative" ref={ageGroupDropdownRef}>
                <button
                  onClick={() => setShowAgeGroupDropdown(!showAgeGroupDropdown)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white w-full sm:w-auto"
                >
                  <span className="text-sm font-medium">
                    {getAgeGroupFilterText()}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 ml-2" />
                </button>

                {showAgeGroupDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      {AGE_GROUP_OPTIONS.map((option) => (
                        <label
                          key={option}
                          className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAgeGroups.includes(option)}
                            onChange={() => handleAgeGroupToggle(option)}
                            className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-[25rem]">
                <input
                  type="text"
                  placeholder="Search by title or keyword..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading lessons...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {!loading && !error && filteredLessons.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-6">
                {searchTerm || selectedAgeGroups.length > 0
                  ? "No lessons found matching your filters."
                  : "No lessons found."}
              </p>
              <button
                onClick={() => setShowSuggestDialog(true)}
                className="inline-flex items-center gap-2 px-8 py-4 text-lg rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors font-medium"
              >
                <LightBulbIcon className="w-6 h-6" />
                Suggest a Lesson Topic
              </button>
            </div>
          )}

          {!loading && !error && filteredLessons.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min">
                {filteredLessons.map((lesson) => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    isCompleted={completedLessons.has(lesson.id)}
                    returnPath="/lessons"
                  />
                ))}
              </div>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowSuggestDialog(true)}
                  className="inline-flex items-center gap-2 px-8 py-4 text-lg rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors font-medium"
                >
                  <LightBulbIcon className="w-6 h-6" />
                  Suggest a Lesson Topic
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <SuggestTopicDialog
        isOpen={showSuggestDialog}
        onClose={() => setShowSuggestDialog(false)}
      />
    </div>
  );
}

export default Lessons;
